import { ActionIcon, Button, Center, Group, Loader, Menu, NumberFormatter, Table, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconDotsVertical, IconEdit, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FirestoreError } from "firebase/firestore";
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Asserts } from "yup";
import {
  HISTORICO_PONTOS_PER_COLABORADOR_KEY,
  deleteHistoricoPontosPerColaborador,
  getHistoricoPontosPerColaborador
} from "@/modules/historico-pontos-per-colaborador/firestore";
import historicoPontosPerColaboradorSchema from "@/modules/historico-pontos-per-colaborador/schema";
import { getIdeia } from "@/modules/ideias/firestore";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";
import { formatDate } from "@/utils/date";

type TGetHistoricoPontosPerColaboradorResponse = Awaited<ReturnType<typeof getHistoricoPontosPerColaborador>>;

/**
 * Get historicoPontosPerColaborador query
 * @param colaborador - Colaborador to get historicoPontosPerColaborador for
 * @param regulamento - regulamento to get historicoPontosPerColaborador for
 * @returns - historicoPontosPerColaborador query
 */
export function useGetHistoricoPontosPerColaborador(
  colaborador: TColaborador | null | undefined,
  regulamento: TRegulamentoIdeias
) {
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);

  return useQuery<TGetHistoricoPontosPerColaboradorResponse, FirestoreError, THistoricoPontosPerColaborador[]>({
    queryKey: [
      activeEmpresaId,
      HISTORICO_PONTOS_PER_COLABORADOR_KEY,
      "getHistoricoPontosPerColaborador",
      { colaboradorId: colaborador?.id, regulamento }
    ],
    queryFn() {
      if (!activeEmpresaId) {
        throw new Error("Nenhuma empresa ativa no momento");
      }

      return getHistoricoPontosPerColaborador(activeEmpresaId, colaborador?.id || "", regulamento);
    },
    select(data) {
      if (data.empty) {
        return [];
      }
      return data.docs.map((document_) => document_.data());
    }
  });
}

/**
 * HistoricoPontuacao type guard
 * @param historico - Historico to check if it is a TOrigemPontuacaoMovimentacaoNaIdeias
 * @returns - True if historico is a TOrigemPontuacaoMovimentacaoNaIdeias
 */
function isHistoricoPontuacaoMovimentacaoNaIdeias(
  historico: TOrigemPontuacao
): historico is TOrigemPontuacaoMovimentacaoNaIdeias {
  return ideiasTypes.includes(historico.type);
}

const ideiasTypes: TOrigemPontuacao["type"][] = [
  "CADASTRO_DE_IDEIAS",
  "APROVACAO_DE_IDEIAS",
  "IMPLEMENTACAO_DE_IDEIAS",
  "PARTICIPACAO_DE_IDEIA_PROPRIA",
  "PARTICIPACAO_DE_IDEIA_TERCEIRO",
  "CANCELADA",
  "REPROVADA"
] as const;

const typesTextMap: Record<TOrigemPontuacao["type"], string> = {
  APROVACAO_DE_IDEIAS: "Aprovação de ideia",
  CADASTRO_DE_IDEIAS: "Cadastro de ideia",
  IMPLEMENTACAO_DE_IDEIAS: "Implementação de ideia",
  PARTICIPACAO_DE_IDEIA_PROPRIA: "Participação de ideia própria",
  PARTICIPACAO_DE_IDEIA_TERCEIRO: "Participação de ideia de terceiros",
  BONIFICACAO: "Bonificação do comitê",
  CANCELADA: "Cancelado",
  REPROVADA: "Reprovado"
};

const GetIdeiaFromId = ({ id }: { id: string }) => {
  const [ideia, setIdeia] = useState<string | null>(null);
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);

  useEffect(() => {
    const fetchIdeia = async () => {
      const ideia = await getIdeia(activeEmpresaId || "", id);
      const createdAt = ideia?.data()?.createdAt;
      setIdeia(createdAt ? formatDate(createdAt) : "");
    };
    void fetchIdeia().catch((error) => console.error("Erro ao buscar ideia", error));
  }, [id, activeEmpresaId]);

  return ideia ? <Text>{ideia}</Text> : <Text>Loading...</Text>;
};

const BonusActionsMenu = ({
  historico,
  canManageBonus
}: {
  historico: THistoricoPontosPerColaborador;
  canManageBonus: boolean;
}) => {
  type TDeleteResponse = Awaited<ReturnType<typeof deleteHistoricoPontosPerColaborador>>;
  const queryClient = useQueryClient();
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);
  const isBonus = historico.type === "BONIFICACAO";

  const { mutate: deleteHistorico, isPending } = useMutation<TDeleteResponse, Error>({
    mutationFn: () => {
      if (!activeEmpresaId) {
        throw new Error("Empresa não selecionada");
      }
      return deleteHistoricoPontosPerColaborador(activeEmpresaId, historico.id, historico.refPath);
    },
    onSuccess() {
      return queryClient.invalidateQueries({
        queryKey: [activeEmpresaId, HISTORICO_PONTOS_PER_COLABORADOR_KEY]
      });
    },
    onError(error) {
      captureException(error, true);
    }
  });

  if (!canManageBonus || !isBonus) {
    return null;
  }

  /**
   *
   */
  function handleEdit() {
    const defaultValues = historicoPontosPerColaboradorSchema.getDefault() as Partial<
      Asserts<typeof historicoPontosPerColaboradorSchema>
    >;
    const item = historicoPontosPerColaboradorSchema.cast(
      { ...defaultValues, ...historico },
      { stripUnknown: true, assert: false }
    );

    modals.openContextModal({
      modal: "add-bonus-form-modal",
      size: "xl",
      innerProps: {
        item,
        mode: "edit"
      }
    });
  }

  /**
   *
   */
  function handleDelete() {
    modals.openConfirmModal({
      title: "Excluir bonificação",
      centered: true,
      children: (
        <Text size="sm">
          Tem certeza que deseja excluir a bonificação de{" "}
          <Text span fw={700}>
            {historico.pontos} pontos
          </Text>
          ?
        </Text>
      ),
      labels: {
        confirm: "Sim",
        cancel: "Não"
      },
      confirmProps: { color: "red" },
      onConfirm: () => {
        void deleteHistorico();
      }
    });
  }

  return (
    <Menu shadow="md" width={200} position="left" withArrow arrowPosition="center">
      <Menu.Target>
        <ActionIcon variant="subtle" aria-label="Ações de bonificação" color="gray">
          <IconDotsVertical size={16} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item leftSection={<IconEdit size={14} />} onClick={handleEdit}>
          Editar
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={handleDelete} disabled={isPending}>
          Excluir
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

const HistoricoPontosRow = ({
  historico,
  canManageBonus
}: {
  historico: THistoricoPontosPerColaborador;
  canManageBonus: boolean;
}) => {
  const isBonus = historico.type === "BONIFICACAO";

  return (
    <Table.Tr>
      <Table.Td>
        {isHistoricoPontuacaoMovimentacaoNaIdeias(historico) && "Ideia"}
        {isBonus && "Bonificação do comitê"}
      </Table.Td>
      <Table.Td>{typesTextMap[historico.type] || historico.type}</Table.Td>
      <Table.Td>
        {isHistoricoPontuacaoMovimentacaoNaIdeias(historico) && historico.ideia ? (
          <Button component={NavLink} to={`/ideias/${historico.ideia.id}`} size="xs" variant="outline">
            Ver ideia
          </Button>
        ) : typeof historico === "object" && "observacao" in historico ? (
          historico.observacao
        ) : (
          ""
        )}
      </Table.Td>
      <Table.Td>
        <Group justify="space-between" wrap="nowrap" gap="xs" style={{ width: "100%" }}>
          <Text c={historico.type === "CANCELADA" || historico.type === "REPROVADA" ? "red" : "green"} fw="bold">
            <NumberFormatter value={historico.pontos} suffix=" pontos" />
          </Text>
          <BonusActionsMenu historico={historico} canManageBonus={canManageBonus} />
        </Group>
      </Table.Td>
      <Table.Td>{formatDate(historico.createdAt)}</Table.Td>
      <Table.Td>
        {isHistoricoPontuacaoMovimentacaoNaIdeias(historico) && historico.ideia ? (
          <GetIdeiaFromId id={historico.ideia.id} />
        ) : (
          ""
        )}
      </Table.Td>
    </Table.Tr>
  );
};

const HistoricoPontosTable = ({
  colaborador,
  regulamento,
  canManageBonus = false
}: {
  colaborador: TColaborador;
  regulamento: TRegulamentoIdeias;
  canManageBonus?: boolean;
}) => {
  const { isLoading, data: historicoPontos } = useGetHistoricoPontosPerColaborador(colaborador, regulamento);

  if (isLoading) {
    return (
      <Center mah={300} py="xl">
        <Loader />
      </Center>
    );
  }

  const isEmpty = !historicoPontos || historicoPontos.length === 0;
  if (isEmpty) {
    return null;
  }

  return (
    <Table highlightOnHover withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Origem da Pontuação</Table.Th>
          <Table.Th>Participação</Table.Th>
          <Table.Th>Origem</Table.Th>
          <Table.Th>Pontos recebidos</Table.Th>
          <Table.Th>Data da Participação</Table.Th>
          <Table.Th>Data de Cadastro Ideia</Table.Th>
        </Table.Tr>
      </Table.Thead>

      {historicoPontos.map((historico) => (
        <HistoricoPontosRow key={historico.id} historico={historico} canManageBonus={canManageBonus} />
      ))}
    </Table>
  );
};

export default HistoricoPontosTable;
